import React, { createContext, forwardRef, useContext, useId } from "react";
import "./Radio.scss";

// Fontaine M15 — Radio + RadioGroup. Visual contract: Style Guide V2 `radio-group` (18px ring, accent dot).
// Native <input type="radio"> inside a <fieldset> gives arrow-key navigation and grouping for free.

const GroupCtx = createContext(null);

/**
 * @param {string} name
 * @param {string} [value]                       controlled selected value
 * @param {(value: string, event) => void} [onChange]
 * @param {ReactNode} [legend]                  group label (visually shown unless hideLegend)
 * @param {"list"|"cards"|"inline"} [layout="list"]   cards = card-radio variant for choices
 */
export function RadioGroup({ name, value, defaultValue, onChange, legend, hideLegend = false, layout = "list", disabled = false, error, className = "", children, ...rest }) {
  const autoId = useId();
  const groupName = name || `ft-radio-${autoId}`;
  const ctx = { name: groupName, value, defaultValue, onChange, disabled, layout };
  const classes = ["ft-radiogroup", `ft-radiogroup--${layout}`, error ? "is-error" : "", className].filter(Boolean).join(" ");
  return (
    <fieldset className={classes} disabled={disabled} aria-invalid={error ? true : undefined} aria-describedby={error ? `${groupName}-error` : undefined} {...rest}>
      {legend != null ? <legend className={`ft-radiogroup__legend${hideLegend ? " is-hidden" : ""}`}>{legend}</legend> : null}
      <GroupCtx.Provider value={ctx}>
        <div className="ft-radiogroup__items">{children}</div>
      </GroupCtx.Provider>
      {error ? <span className="ft-radiogroup__error" id={`${groupName}-error`} role="alert">{error}</span> : null}
    </fieldset>
  );
}

/**
 * @param {string} value
 * @param {ReactNode} label
 * @param {ReactNode} [description]
 */
const Radio = forwardRef(function Radio({ value, label, description, disabled = false, id, className = "", ...rest }, ref) {
  const g = useContext(GroupCtx) || {};
  const autoId = useId();
  const inputId = id || `ft-radio-${autoId}`;
  const controlled = g.value !== undefined;
  const classes = ["ft-radio", g.layout === "cards" ? "ft-radio--card" : "", disabled || g.disabled ? "is-disabled" : "", className].filter(Boolean).join(" ");
  return (
    <label className={classes} htmlFor={inputId}>
      <input
        ref={ref}
        id={inputId}
        type="radio"
        className="ft-radio__input"
        name={g.name}
        value={value}
        checked={controlled ? g.value === value : undefined}
        defaultChecked={!controlled && g.defaultValue !== undefined ? g.defaultValue === value : undefined}
        disabled={disabled}
        aria-labelledby={`${inputId}-label`}
        aria-describedby={description ? `${inputId}-desc` : undefined}
        onChange={(e) => g.onChange?.(value, e)}
        {...rest}
      />
      <span className="ft-radio__dot" aria-hidden="true" />
      <span className="ft-radio__text">
        <span className="ft-radio__label" id={`${inputId}-label`}>{label}</span>
        {description ? <span className="ft-radio__desc" id={`${inputId}-desc`}>{description}</span> : null}
      </span>
    </label>
  );
});

export default Radio;
